import React from "react";
import { Tabs, Card, Row, Col, Empty, Typography, Tag } from "antd";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { CalendarOutlined, EnvironmentOutlined } from "@ant-design/icons";

import { getData } from "../apiUtils";
import Loading from "../components/Loading";
import Error from "../components/Error";
import {
  CATEGORIES,
  groupByCategory,
  isUpcoming,
  isExternal,
  coverInfo,
} from "../workshopUtils";
import "./WorkshopArchive.less";

const { TabPane } = Tabs;
const { Title, Paragraph } = Typography;

const formatRange = (item) => {
  if (!item.startDate) return null;

  const opts = { day: "numeric", month: "short", year: "numeric" };
  const start = new Date(item.startDate).toLocaleDateString("en-IN", opts);
  if (!item.endDate) return start;

  const end = new Date(item.endDate).toLocaleDateString("en-IN", opts);
  return start === end ? start : `${start} – ${end}`;
};

function EditionCard({ item }) {
  const cover = coverInfo(item);
  // Real dates once they are filled in in Strapi; until then the free-text
  // `dates` string the cover has always carried.
  const dates = formatRange(item) || cover.dates;

  const card = (
    <Card
      hoverable
      className="archive-card"
      cover={
        <div className="archive-card-media">
          {cover.image ? (
            <img src={cover.image} alt={cover.alt} loading="lazy" />
          ) : (
            // Keeps every card the same height when an edition has no poster.
            <div className="archive-card-media-empty" />
          )}
          {isUpcoming(item) && (
            <Tag color="red" className="archive-card-tag">
              Upcoming
            </Tag>
          )}
        </div>
      }
    >
      <div className="archive-card-title">{cover.title}</div>
      {dates && (
        <div className="archive-card-meta">
          <CalendarOutlined /> {dates}
        </div>
      )}
      {cover.venue && (
        <div className="archive-card-meta">
          <EnvironmentOutlined /> {cover.venue}
        </div>
      )}
    </Card>
  );

  return isExternal(item.url) ? (
    <a href={item.url} target="_blank" rel="noreferrer noopener">
      {card}
    </a>
  ) : (
    <Link to={item.url}>{card}</Link>
  );
}

export default function WorkshopArchive() {
  const location = useLocation();
  const history = useHistory();

  // `/workshop-pages` rather than `/workshop-menu`, because only this one
  // carries the cover blocks the cards need. App.js already fetches it to
  // build the routes, so react-query serves it from cache — no extra request.
  const { isLoading, error, data } = useQuery(
    "workshop-pages",
    getData({ apiRoute: "/workshop-pages" })
  );

  if (isLoading) return <Loading />;
  if (error) return <Error err={error} />;

  const items = data || [];
  const groups = groupByCategory(items);

  // Only categories that actually have editions get a tab.
  const populated = CATEGORIES.filter(
    ({ key }) => groups[key] && groups[key].all.length
  );

  const requested = new URLSearchParams(location.search).get("category");
  const activeKey =
    populated.some(({ key }) => key === requested) && requested
      ? requested
      : (populated[0] || {}).key;

  const onTabChange = (key) => {
    history.replace(`/workshop-archive?category=${key}`);
  };

  return (
    <div className="workshop-archive">
      <Title level={2} className="workshop-archive-title">
        Event Archive
      </Title>
      <Paragraph className="workshop-archive-intro">
        Every edition held so far. Select an event to open its page.
      </Paragraph>

      {populated.length ? (
        <Tabs activeKey={activeKey} onChange={onTabChange} size="large">
          {populated.map(({ key, label }) => (
            <TabPane tab={`${label} (${groups[key].all.length})`} key={key}>
              <Row gutter={[24, 24]}>
                {groups[key].all.map((item) => (
                  <Col xs={24} sm={12} lg={8} key={item.id}>
                    <EditionCard item={item} />
                  </Col>
                ))}
              </Row>
            </TabPane>
          ))}
        </Tabs>
      ) : (
        <Empty description="No events published yet" />
      )}
    </div>
  );
}
